/**
 * Soil Sample Events - Betrieb Müller (v2)
 *
 * NOW Section - Urgent aggregate event for soil samples.
 * Per spec: only aggregate event shown, no individual singles.
 *
 * CARD STRUCTURE (top to bottom):
 * 1. Event Summary - one sentence, no object details
 * 2. Affected Objects - single source of truth for object-level info
 * 3. Primary Actions - immediately after objects
 * 4. Why This Matters - collapsible, just the rule
 * 5. Calculation Basis - collapsed by default
 * 6. Audit Actions - bottom, if relevant
 *
 * ANTI-PATTERNS AVOIDED:
 * - No missingItems (redundant with affectedEntities)
 * - No field names/dates in description
 * - No object-level info in reasoning.summary
 */

import type { MockControlEvent } from '@hofsync/design-system';
import { hoursAgo, daysFromNow } from '../farm-profile';
import { DUEV_4_4 } from '../../legal';
import { ALL_FIELDS } from '../entities/fields';

/**
 * Event 1 (NOW - Shown): Bodenproben Aggregate (URGENT)
 *
 * This is the primary event shown for soil samples.
 * No individual field events are shown - aggregate replaces them.
 */
export const EVENT_BODENPROBEN_NOW: MockControlEvent = {
  id: 'evt-bodenproben-now',
  type: 'attention-required',
  priority: 'urgent',
  title: 'Bodenproben: Schläge brauchen Aufmerksamkeit',
  subtitle: 'Bodenuntersuchung',
  timestamp: hoursAgo(2),
  relevantDate: daysFromNow(0), // Most urgent item is overdue
  relatedEntity: { name: 'Bodenproben', href: '#soil-samples' },
  categories: ['duev', 'bodenproben'],
  controlType: 'Bodenuntersuchung',
  controlReadiness: {
    status: 'not-ready',
    metCount: 18,
    totalCount: 21,
    label: '18 von 21 aktuell',
  },

  // === AFFECTED OBJECTS (Single source of truth) ===
  // Sorted by severity (most critical first)
  // Contains: name, size, status, deadline, relative urgency
  isAggregate: true,
  hideAggregateBadge: true, // Count shown in controlReadiness label instead
  affectedEntities: [
    {
      id: 'field-grosses-feld',
      name: 'Großes Feld (12,4 ha)',
      type: 'field',
      severity: 'critical',
      status: 'Überfällig seit 10 Monaten',
      dueDate: new Date('2025-03-14'),
      action: { label: 'Probe beauftragen', href: '/my-farm/fields/grosses-feld' },
    },
    {
      id: 'field-waldacker',
      name: 'Waldacker (6,8 ha)',
      type: 'field',
      severity: 'warning',
      status: 'Fällig in 53 Tagen',
      dueDate: new Date('2026-03-20'),
      action: { label: 'Probe beauftragen', href: '/my-farm/fields/waldacker' },
    },
    {
      id: 'field-bachwiese',
      name: 'Bachwiese (4,2 ha)',
      type: 'field',
      severity: 'warning',
      status: 'Fällig in 89 Tagen',
      dueDate: new Date('2026-04-25'),
      action: { label: 'Probe beauftragen', href: '/my-farm/fields/bachwiese' },
    },
  ],
  domainLink: {
    label: 'Alle Flächen anzeigen',
    href: '/farm?lense=fields',
  },


  // === WHY THIS MATTERS (collapsible) ===
  // Just the rule - no object names, no dates, no urgency
  reasoning: {
    summary: 'Phosphat-Bodenproben sind alle 6 Jahre vor der P-Düngung erforderlich.',
    legalBasis: [DUEV_4_4],
    actionRecommendation:
      'Beauftragen Sie neue Bodenproben bei einem akkreditierten Labor (z.B. LUFA). ' +
      'Nach Erhalt der Ergebnisse hier hochladen – HofSync ordnet sie automatisch zu.',
    // === CALCULATION BASIS (collapsed by default) ===
    // Machine explanation for trust/audit
    factors: [
      { label: 'Schläge gesamt', value: '21' },
      { label: 'Davon aktuell', value: '18' },
      { label: 'Handlungsbedarf', value: '3' },
      { label: 'Berechnungsregel', value: 'Letzte Probe + 6 Jahre' },
    ],
    confidence: 'high',
    // NO missingItems - redundant with affectedEntities

    // === DETAILED CALCULATION FOR SIDE PANEL ===
    calculationDetails: {
      header: {
        title: 'Berechnungsgrundlage – Bodenprobe',
        ruleName: 'Phosphat-Bodenuntersuchung',
        statusBadge: 'Automatisch geprüft',
        timestamp: 'Stand: heute',
      },
      legalBasis: {
        regulationName: 'Düngeverordnung',
        paragraph: 'DüV § 4 Abs. 4',
        summary:
          'Phosphat-Bodenproben sind mindestens alle 6 Jahre vor der P-Düngung erforderlich. ' +
          'Bei Überschreitung sind Bodenuntersuchungen durchzuführen, bevor phosphathaltige Düngemittel ausgebracht werden dürfen.',
        sourceUrl:
          'https://www.gesetze-im-internet.de/d_v_2017/__4.html',
      },
      facts: [
        {
          label: 'Schlag',
          value: 'Großes Feld (12,4 ha)',
          entityId: 'field-grosses-feld',
          entityType: 'field',
        },
        {
          label: 'Letzte Bodenprobe',
          value: '14.03.2019',
          source: 'LUFA Nord-West',
        },
        {
          label: 'Betriebstyp',
          value: 'Ackerbau > 15 ha',
        },
        {
          label: 'Bodenart',
          value: 'Lehmiger Sand (Sl3)',
        },
        {
          label: 'P-Versorgungsstufe',
          value: 'C (anzustreben)',
        },
      ],
      logic: {
        ruleExpression: 'Letzte Bodenprobe + 6 Jahre = Fälligkeitsdatum',
        steps: [
          { description: 'Datum der letzten Bodenprobe: 14.03.2019' },
          { description: '+ 6 Jahre Gültigkeitsdauer' },
          { description: '= Fälligkeit: 14.03.2025', isDecisive: true },
          { description: 'Heute: 27.01.2026 → Überfällig seit 10 Monaten' },
        ],
      },
      result: {
        status: 'Überfällig',
        severity: 'critical',
        deadline: '14.03.2025',
        deviation: 'Überfällig seit 10 Monaten',
      },
      evidence: [
        {
          name: 'Bodenprobe Großes Feld (2019)',
          type: 'document',
          id: 'doc-bp-grosses-feld',
        },
        {
          name: 'Schlag: Großes Feld',
          type: 'entity',
          id: 'field-grosses-feld',
          entityType: 'field',
        },
      ],
    },
  },

  entities: ALL_FIELDS,
};

/**
 * All soil sample events
 * Note: Only aggregate event - no individual field events per v2 spec
 */
export const SOIL_SAMPLE_EVENTS: MockControlEvent[] = [EVENT_BODENPROBEN_NOW];
